// 🎟️ Imports
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { LocalNotifications } from '@capacitor/local-notifications'
import { getToken, onMessage, isSupported } from 'firebase/messaging'
import Toastify from 'toastify-js'
import 'toastify-js/src/toastify.css'
import { getStoredItem, setStoredItem } from '@/services/utils'
import { loadConfig } from '@/configLoader'
import { getFirestore, doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { useOAuthService } from '@/services/OAuthService'
import { getCustomer } from '@/assets/Branding/branding.js'
import { sha256 } from 'js-sha256'
import router from '@/router'

let app = null
let messaging = null
let webPushSupported = false
let listenersInitialized = false

const isDevEnv = () => {
  const platform = Capacitor.getPlatform()

  // ✅ Native platforms: use Capacitor.DEBUG
  if (platform === 'android' || platform === 'ios') {
    return !!Capacitor.DEBUG
  }

  // ✅ Web: check localhost or 192.168.*
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  return hostname.includes('localhost') || hostname.startsWith('192.168.')
}
const getEnvironmentLabel = () => (isDevEnv() ? 'development' : 'production')

const toast = (msg, type = 'default') => {
  const styles = {
    success: { background: '#4caf50', color: '#fff' },
    error: { background: '#f44336', color: '#fff' },
    default: { background: '#333', color: '#fff' },
  }
  Toastify({
    text: msg,
    duration: 3000,
    gravity: 'bottom',
    position: 'center',
    close: true,
    stopOnFocus: true,
    style: {
      ...styles[type],
      padding: '10px 20px',
      borderRadius: '8px',
      fontFamily: 'Poppins, sans-serif',
    },
  }).showToast()
}

const generateStableDeviceId = async (subId, email, customer, environment, platform) => {
  if (platform === 'ios' || platform === 'web') {
    const info = await Device.getInfo()
    const base = `${subId.toLowerCase()}:${email.toLowerCase()}:${customer.toLowerCase()}_${environment.toLowerCase()}:${info.model}:${info.name}`
    return sha256(base)
  }
  const device = await Device.getId()
  return device.identifier
}

const isSafariMac = () => {
  const ua = navigator.userAgent
  const isMac = navigator.platform === 'MacIntel'
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
  return isMac && isSafari
}

export const initFirebaseIfNeeded = async () => {
  if (app && messaging) return app

  const config = await loadConfig()
  const customer = await getCustomer()
  const customerId = customer?.toLowerCase() || 'default'
  if (!config?.firebaseConfig) throw new Error('❌ Missing Firebase config')

  const { initializeApp, getApps } = await import('firebase/app')
  app = getApps().length ? getApps()[0] : initializeApp(config.firebaseConfig)

  webPushSupported =
    Capacitor.getPlatform() === 'web' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && (await isSupported())

  if (webPushSupported) {
    const { getMessaging } = await import('firebase/messaging')
    messaging = getMessaging(app)
  }

  getFirestore(app)
  return app
}

export const clearFirebaseApp = () => {
  app = null
  messaging = null
  listenersInitialized = false
}

export const requestNotificationPermission = async () => {
  const platform = Capacitor.getPlatform()
  const environment = getEnvironmentLabel()
  const topic = isDevEnv() ? 'news-dev' : 'news'

  let permissionGranted = false

  try {
    if (platform === 'android' || platform === 'ios') {
      await FirebaseMessaging.requestPermissions()
      await LocalNotifications.requestPermissions()
      permissionGranted = true
    } else {
      const result = await Notification.requestPermission()
      if (result !== 'granted') return toast('❌ Notification permission denied', 'error')
      permissionGranted = true
    }

    await initFirebaseIfNeeded()

    let retries = 0
    while (!messaging && retries < 5) {
      await new Promise(resolve => setTimeout(resolve, 300))
      retries++
    }
    if (!messaging && platform === 'web') {
      //return toast('❌ Push messaging failed to initialize', 'error')
      console.warn('❌ Push messaging failed to initialize')
    }

    const db = getFirestore(app)
    const deviceInfo = await Device.getInfo()
    const { getUserProfile } = useOAuthService()
    const profile = await getUserProfile()
    const subId = profile?.sub
    const email = profile?.email
    const customer = await getCustomer()
    const customerId = customer?.toLowerCase() || 'default'
    const envLabel = isDevEnv() ? 'dev' : 'prod'

    if (!subId || !email) return console.warn('⚠️ Missing subId or email')

    const deviceId = await generateStableDeviceId(subId, email, customerId, envLabel, platform)

    const timestamp = new Date().toISOString()
    await setDoc(doc(db, `users/${email.toLowerCase()}`), { sub: subId, email, updatedAt: timestamp }, { merge: true })
    await setDoc(doc(db, `userTokens/${subId}`), { email, createdAt: timestamp }, { merge: true })

    const currentSub = await getStoredItem('fcm_sub_id')
    const isDifferentUser = currentSub && currentSub !== subId
    if (isDifferentUser) {
      await deleteDoc(doc(db, `userTokens/${currentSub}/devices/${deviceId}`))
    }

    const writeTokenToFirestore = async token => {
      const ref = doc(db, `userTokens/${subId}/devices/${deviceId}`)
      const existing = await getDoc(ref)
      const payload = {
        token,
        platform,
        topic,
        email,
        updatedAt: new Date().toISOString(),
        environment,
        model: deviceInfo.model || 'unknown',
        name: deviceInfo.name || 'unknown',
      }
      if (platform === 'web') {
        if (!existing.exists() || existing.data()?.token !== token) {
          await setDoc(ref, payload)
          //toast('✅ Token updated (web)', 'success')
        } else {
          console.log('✅ Web token unchanged — skipping write')
        }
      } else {
        await setDoc(ref, payload)
        //toast('✅ Token updated (native)', 'success')
      }
    }

    let token = await getStoredItem('fcm_token')
    if (!token) {
      if (platform === 'android' || platform === 'ios') {
        const result = await FirebaseMessaging.getToken()
        token = result.token
        await FirebaseMessaging.subscribeToTopic({ topic })
        await setStoredItem('fcm_token', token)
      } else if (webPushSupported) {
        const registration = await navigator.serviceWorker.ready
        token = await getToken(messaging, {
          vapidKey: 'BGvgRnARZd8nE_AjOilPHfpL_geaopYVjWuu0r5Eq-lYnc_EEABErcqCHpsq3Fn_hVGx-631-p1h7tAqEM1NryU',
          serviceWorkerRegistration: registration,
        })
        await setStoredItem('fcm_token', token)
      }
    } else {
      console.log('✅ Using cached FCM token')
    }

    if (token) {
      await setStoredItem('fcm_token', token)
      await setStoredItem('fcm_sub_id', subId)
      await writeTokenToFirestore(token)
    }
  } catch (err) {
    console.error('🚫 Notification setup error:', err)
    //toast('❌ Error requesting permissions', 'error')
  }
}

export const setupForegroundListener = async () => {
  await initFirebaseIfNeeded()
  const { isLoggedIn } = await useOAuthService()
  if (listenersInitialized || !isLoggedIn) return

  if (Capacitor.isNativePlatform()) {
    FirebaseMessaging.addListener('notificationReceived', async ({ notification }) => {
      const { operatingSystem } = await Device.getInfo()
      const isIos = operatingSystem === 'ios'
      if (isIos) {
        return
      }
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Math.floor(Date.now() % 2147483647),
            title: notification.title || 'Notification',
            body: notification.body || '',
            schedule: { at: new Date(Date.now() + 100) },
            sound: 'notification',
            largeIcon: 'ic_notification',
          },
        ],
      })
    })
    FirebaseMessaging.addListener('pushNotificationReceived', async ({ notification }) => {
      const { operatingSystem } = await Device.getInfo()
      const isIos = operatingSystem === 'ios'
      if (isIos) {
        return
      }
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Math.floor(Date.now() % 2147483647),
            title: notification.title || 'Notification',
            body: notification.body || '',
            schedule: { at: new Date(Date.now() + 100) },
            sound: 'notification',
            largeIcon: 'ic_notification',
          },
        ],
      })
    })
    FirebaseMessaging.addListener('localNotificationReceived', async ({ notification }) => {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Math.floor(Date.now() % 2147483647),
            title: notification.title || 'Notification',
            body: notification.body || '',
            schedule: { at: new Date(Date.now() + 100) },
            sound: 'notification',
            largeIcon: 'ic_notification',
          },
        ],
      })
    })
  } else if (webPushSupported && messaging) {
    onMessage(messaging, payload => {
      console.log('📨 Foreground message received:', payload)
      //toast(payload?.notification?.body || 'New notification!')

      navigator.serviceWorker.ready.then(reg => {
        if (!payload.notification) return
        reg.showNotification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/favicon.ico',
          data: payload.data || {},
        })
      })
    })
  }
  listenersInitialized = true
}
